import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { pollImportJob } from "@/lib/importJob";
import { FileUp, Layers, Plus, Loader2, Trash2, CheckCircle2, XCircle, Fingerprint, Sparkles } from "lucide-react";

const inputCls = "w-full bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none placeholder:text-zinc-600";

export const MultiVariantImport = ({ services, onImported }) => {
  const [brand, setBrand] = useState("");
  const [ecu, setEcu] = useState("");
  const [tunedWith, setTunedWith] = useState("");
  const [orig, setOrig] = useState(null);
  const [variants, setVariants] = useState([]); // {file, services:[], suggested:[], power_hp}
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const origRef = useRef(null);
  const modRef = useRef(null);

  const addMods = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const rows = files.map((file) => ({ file, services: [], suggested: [], power_hp: "", detecting: true }));
    setVariants((v) => [...v, ...rows]);
    // pre-detect services per file
    for (const row of rows) {
      try {
        const fd = new FormData();
        fd.append("filename", row.file.name);
        const { data } = await api.post("/admin/solutions/suggest_services", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setVariants((v) => v.map((r) => (r.file === row.file ? { ...r, services: data.services || [], suggested: data.services || [], detecting: false } : r)));
      } catch {
        setVariants((v) => v.map((r) => (r.file === row.file ? { ...r, detecting: false } : r)));
      }
    }
  };

  const toggleSvc = (idx, id) =>
    setVariants((v) => v.map((r, i) => (i === idx ? { ...r, services: r.services.includes(id) ? r.services.filter((x) => x !== id) : [...r.services, id] } : r)));
  const setHp = (idx, val) => setVariants((v) => v.map((r, i) => (i === idx ? { ...r, power_hp: val } : r)));
  const removeVariant = (idx) => setVariants((v) => v.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!orig) return toast.error("Ajoutez le fichier ORIGINAL (stock)");
    if (variants.length === 0) return toast.error("Ajoutez au moins un fichier modifié (variante)");
    if (variants.some((v) => v.services.length === 0)) return toast.error("Chaque variante doit avoir au moins une solution cochée");
    const bad = variants.find((v) => v.file.size !== orig.size);
    if (bad) return toast.error(`"${bad.file.name}" n'a pas la même taille que l'original`);
    setBusy(true);
    setResults(null);
    const fd = new FormData();
    fd.append("original_file", orig);
    fd.append("brand", brand);
    fd.append("ecu", ecu);
    fd.append("tuned_with", tunedWith || "");
    variants.forEach((v) => fd.append("modified_files", v.file));
    fd.append("variants", JSON.stringify(variants.map((v) => ({ filename: v.file.name, services: v.services, power_hp: v.power_hp || null }))));
    try {
      const { data: started } = await api.post("/admin/solutions/import_multi", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const job = await pollImportJob(started.job_id);
      if (job.status === "failed") {
        toast.error(job.fail_summary || job.error || "Erreur d'import");
      } else {
        const data = job.result || { ok: 0, total: 0, results: [] };
        setResults(data);
        if (data.ok > 0) toast.success(`${data.ok}/${data.total} variante(s) importée(s) ✅`);
        if (data.ok < data.total) toast.error(`${data.total - data.ok} variante(s) en erreur`);
        if (data.ok === data.total) {
          setOrig(null); setVariants([]); setBrand(""); setEcu(""); setTunedWith("");
        }
        onImported?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erreur d'import");
    } finally { setBusy(false); }
  };

  return (
    <div className="glass rounded-md p-6 mb-6" data-testid="multi-variant-import">
      <div className="flex items-center gap-2 mb-1">
        <Layers className="w-5 h-5 text-aurora-cyan" />
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Importer un original + ses variantes</h3>
      </div>
      <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-snug">
        Déposez <b className="text-white">une seule fois</b> le fichier ORIGINAL, puis ajoutez autant de fichiers <b>modifiés</b> que voulu (Stage 1, DPF, DPF+AdBlue…). Chaque variante devient une solution distincte partageant le même original. Les solutions sont <b className="text-aurora-cyan">pré-détectées</b> d'après le nom du fichier.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Marque (info)</label>
          <input data-testid="multi-brand" className={inputCls} placeholder="Audi" value={brand} onChange={(e) => setBrand(e.target.value)} />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">ECU (info)</label>
          <input data-testid="multi-ecu" className={inputCls} placeholder="Bosch EDC17C64" value={ecu} onChange={(e) => setEcu(e.target.value)} />
        </div>
      </div>

      <div className="mb-4">
        <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Logiciel utilisé <span className="text-zinc-600">(admin · optionnel · commun aux variantes)</span></label>
        <input data-testid="multi-tuned-with" className={inputCls} placeholder="Ex. WinOLS" value={tunedWith} onChange={(e) => setTunedWith(e.target.value)} />
      </div>

      {/* Original */}
      <input ref={origRef} type="file" className="hidden" data-testid="multi-orig-input" onChange={(e) => setOrig(e.target.files[0])} />
      <button onClick={() => origRef.current?.click()} className="w-full flex items-center gap-3 p-3 rounded-sm border border-dashed border-white/15 hover:border-aurora-cyan/50 transition-colors mb-4">
        <FileUp className="w-5 h-5 text-zinc-400" />
        <span className="font-mono text-xs text-zinc-300 truncate flex-1 text-left">{orig ? orig.name : "Fichier ORIGINAL (stock) — déposé une seule fois"}</span>
        {orig && <span className="font-mono text-[10px] text-aurora-purple flex items-center gap-1 shrink-0"><Fingerprint className="w-3 h-3" /> {orig.size.toLocaleString("fr-FR")} o</span>}
      </button>

      {/* Variants */}
      <div className="space-y-3 mb-4">
        {variants.map((v, idx) => {
          const sizeOk = orig ? v.file.size === orig.size : true;
          return (
            <div key={idx} data-testid={`variant-row-${idx}`} className={`rounded-sm border p-3 ${sizeOk ? "border-white/10 bg-white/5" : "border-signal-error/50 bg-signal-error/5"}`}>
              <div className="flex items-center gap-2 mb-2">
                <FileUp className="w-4 h-4 text-signal-success shrink-0" />
                <span className="font-mono text-xs text-white truncate flex-1">{v.file.name}</span>
                <span className={`font-mono text-[10px] shrink-0 ${sizeOk ? "text-zinc-500" : "text-signal-error"}`}>
                  {v.file.size.toLocaleString("fr-FR")} o{!sizeOk && " · ≠ original"}
                </span>
                <button data-testid={`variant-remove-${idx}`} onClick={() => removeVariant(idx)} className="w-7 h-7 grid place-items-center border border-white/15 rounded-sm hover:border-signal-error hover:text-signal-error transition-colors shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {v.detecting ? (
                <div className="flex items-center gap-2 font-mono text-[10px] text-zinc-500"><Loader2 className="w-3 h-3 animate-spin" /> Détection des solutions…</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {services.map((s) => {
                    const on = v.services.includes(s.id);
                    const wasSuggested = v.suggested.includes(s.id);
                    return (
                      <button key={s.id} data-testid={`variant-svc-${idx}-${s.id}`} onClick={() => toggleSvc(idx, s.id)}
                        className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest rounded-sm border transition-colors flex items-center gap-1 ${on ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                        {on && wasSuggested && <Sparkles className="w-2.5 h-2.5" />}
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 shrink-0">Puissance (ch)</label>
                <input data-testid={`variant-hp-${idx}`} type="number" min="0" placeholder="optionnel" value={v.power_hp}
                  onChange={(e) => setHp(idx, e.target.value)}
                  className="w-28 bg-black/50 border border-white/10 text-white px-2 py-1 rounded-sm text-xs focus:border-aurora-cyan focus:outline-none placeholder:text-zinc-600" />
              </div>
            </div>
          );
        })}
      </div>

      <input ref={modRef} type="file" multiple className="hidden" data-testid="multi-add-mod-input" onChange={(e) => { addMods(e.target.files); e.target.value = ""; }} />
      <div className="flex flex-wrap gap-2 items-center">
        <button data-testid="multi-add-mod-btn" onClick={() => modRef.current?.click()} className="flex items-center gap-2 px-4 py-2.5 border border-signal-success/40 text-signal-success font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-signal-success/10 transition-colors">
          <Plus className="w-4 h-4" /> Ajouter des fichiers modifiés
        </button>
        <button data-testid="submit-multi-btn" onClick={submit} disabled={busy || !orig || variants.length === 0} className="flex items-center gap-2 px-6 py-2.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cyan-300 transition-colors shadow-glow disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />} Créer {variants.length || ""} variante{variants.length > 1 ? "s" : ""}
        </button>
      </div>

      {results && (
        <div className="mt-4 space-y-1.5" data-testid="multi-results">
          {results.results.map((r, i) => (
            <div key={i} className="flex items-center gap-2 font-mono text-[11px]">
              {r.status === "ok" ? <CheckCircle2 className="w-3.5 h-3.5 text-signal-success shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-signal-error shrink-0" />}
              <span className="text-zinc-300 truncate">{r.filename}</span>
              {r.status === "ok"
                ? <span className="text-signal-success shrink-0">{r.mode === "diff" ? "diff ✓" : "clone"} · {(r.services || []).join(", ")}</span>
                : <span className="text-signal-error shrink-0">{r.detail}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
